import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Right-side slide-in drawer.
//
// Usage:
//   <SideDrawer open={open} onClose={handleClose} width={540}>
//     <SideDrawer.Header eyebrow="…" title="…" sub="…" onClose={handleClose} />
//     <SideDrawer.Body> … </SideDrawer.Body>
//     <SideDrawer.Footer> … </SideDrawer.Footer>
//   </SideDrawer>
//
// Behaviour:
//   - Escape key closes (delegates to onClose so callers can intercept for
//     unsaved-changes prompts)
//   - Clicking the backdrop closes via the same onClose
//   - Focus is moved into the drawer on open and trapped while open (Tab /
//     Shift+Tab cycle within drawer-focusable elements)
//   - Body scroll is locked while open
//   - Mounted via createPortal so the drawer always sits above page chrome
export default function SideDrawer({ open, onClose, width = 540, children, ariaLabel }) {
  const drawerRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  // Below 680px the drawer goes full-screen (account brief §8) — a 480px side
  // panel is cramped on a phone. 100dvh (not 100vh) so mobile browser chrome
  // doesn't clip it. Above 680px it's the configured side panel, unchanged for
  // every other consumer.
  const [isMobile, setIsMobile] = React.useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 680px)');
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Track previously-focused element so we can restore focus on close.
  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement;
    return () => {
      const el = previouslyFocusedRef.current;
      if (el && typeof el.focus === 'function') el.focus();
    };
  }, [open]);

  // Move focus into the drawer once it's mounted + open. requestAnimationFrame
  // gives the slide-in transition a tick to start so the focused element
  // isn't off-screen at the moment of focus.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      const el = drawerRef.current;
      if (!el) return;
      const focusable = el.querySelector(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) focusable.focus();
      else el.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Lock body scroll while open so background content doesn't drift under
  // the backdrop.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  // Escape closes (via onClose so callers can intercept) + focus trap on Tab.
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const el = drawerRef.current;
      if (!el) return;
      const focusables = Array.from(el.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(n => n.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Render even when closed so the slide-out transition has somewhere to
  // animate to. The transform handles visibility; pointerEvents:none on
  // the backdrop when closed stops it eating clicks.
  return createPortal(
    <>
      <div
        onClick={() => onClose?.()}
        style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(0,0,0,0.6)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'all' : 'none',
          transition: 'opacity 0.2s',
        }} />
      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || 'Drawer'}
        tabIndex={-1}
        style={{
          position: 'fixed', top: 0, right: 0,
          ...(isMobile
            ? { bottom: 'auto', height: '100dvh', width: '100vw' }
            : { bottom: 0, width, maxWidth: '100vw' }),
          background: '#F3F6EA',
          borderLeft: '1px solid #D4D9C6',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease-out',
          zIndex: 101,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}>
        {/* Top accent stripe — matches the 30°-style brand stripe used on
            CompleteBriefPanel. */}
        <div style={{
          height: 3,
          background: 'linear-gradient(90deg, #92D400 0%, #92D400 30%, transparent 30%)',
        }} />
        {children}
      </aside>
    </>,
    document.body
  );
}

function Header({ eyebrow, title, sub, onClose }) {
  return (
    <div className="px-7 pt-5 pb-4 border-b border-line flex items-start justify-between gap-4 flex-shrink-0">
      <div className="min-w-0 flex-1">
        {eyebrow && (
          <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.2em] uppercase text-[#51534A] mb-1.5">
            {eyebrow}
          </div>
        )}
        <div className="font-['Metropolis'] text-xl font-bold text-ink mb-1 truncate">{title}</div>
        {sub && <div className="font-['Metropolis'] text-xs text-[#51534A]">{sub}</div>}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close drawer"
        className="w-8 h-8 flex items-center justify-center bg-transparent border border-line rounded text-[#51534A] hover:text-ink hover:border-[#DADCCD] transition-colors flex-shrink-0">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

function Body({ children }) {
  return (
    <div className="flex-1 overflow-y-auto px-7 py-6">
      {children}
    </div>
  );
}

function Footer({ children }) {
  return (
    <div className="px-7 py-3.5 border-t border-line bg-black/[0.03] flex-shrink-0">
      {children}
    </div>
  );
}

SideDrawer.Header = Header;
SideDrawer.Body = Body;
SideDrawer.Footer = Footer;
